import { Platform } from 'react-native';
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getDatabase, Database } from 'firebase/database';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import {
  getAnalytics as getWebAnalytics,
  logEvent as logWebEvent,
  setAnalyticsCollectionEnabled as setWebAnalyticsCollectionEnabled,
  Analytics,
} from 'firebase/analytics';
import {
  getFirestore as getWebFirestore,
  collection as webCollection,
  doc as webDoc,
  getDoc as webGetDoc,
  getDocs as webGetDocs,
  setDoc as webSetDoc,
  updateDoc as webUpdateDoc,
  deleteDoc as webDeleteDoc,
  addDoc as webAddDoc,
  query as webQuery,
  where as webWhere,
  orderBy as webOrderBy,
  limit as webLimit,
  onSnapshot as webOnSnapshot,
  Timestamp as webTimestamp,
} from 'firebase/firestore';

// Firebase configuration
// Web OAuth uses Firebase v5 compat (via authService.web.ts) for compatibility with hosted handler
// Native apps use Firebase v12 modular SDK
const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY || 'AIzaSyAIIkNqT_xn95ZoHAsLDb0eli8b4Vz5cbw',
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN || 'ai.mentorist.app',
  databaseURL: process.env.EXPO_PUBLIC_FIREBASE_DATABASE_URL || 'https://habitcoach-9c912.firebaseio.com',
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID || 'habitcoach-9c912',
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET || 'habitcoach-9c912.appspot.com',
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '349271191157',
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID || '1:349271191157:web:4020f5f18e7ebe8bbe523c',
  measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID || 'G-KW9NGFCTXH',
};

// Initialize Firebase for web/native (modular only)
let firebaseApp: FirebaseApp | any;
let firestore: any;
let auth: Auth | any;
let database: Database | any;
let functions: Functions | any;
let analytics: Analytics | any;
let getAnalyticsInstance: () => Analytics | any;
let logAnalyticsEvent: (eventName: string, params?: Record<string, any>) => Promise<void> | void;
let setAnalyticsCollectionEnabledFn: (enabled: boolean) => Promise<void> | void;

const initWebFirebase = () => {
  const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  firebaseApp = app;
  firestore = getWebFirestore(app);
  auth = getAuth(app);
  database = getDatabase(app);
  functions = getFunctions(app);
  analytics = getWebAnalytics(app);
  getAnalyticsInstance = () => analytics || getWebAnalytics(firebaseApp || getApp());
  logAnalyticsEvent = (eventName: string, params?: Record<string, any>) =>
    logWebEvent(getAnalyticsInstance(), eventName, params);
  setAnalyticsCollectionEnabledFn = (enabled: boolean) =>
    setWebAnalyticsCollectionEnabled(getAnalyticsInstance(), enabled);
  
  // Set auth persistence to LOCAL so users (OAuth and anonymous) stay logged in across refreshes
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('✅ Firebase Auth persistence set to LOCAL (persists across page refreshes)');
    })
    .catch((error) => {
      console.error('⚠️ Failed to set auth persistence:', error);
    });
  
  // Connect to Functions emulator in development
  const useLocalFunctions = process.env.EXPO_PUBLIC_USE_LOCAL_FUNCTIONS === 'true';
  if (useLocalFunctions && functions && !(functions as any)._delegate?._customHost) {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    console.log('✅ Connected to Functions emulator at localhost:5001');
  }
  
  console.log('Firebase initialized successfully for web');
};

try {
  if (Platform.OS === 'web') {
    initWebFirebase();
    
    // Enable Firestore debug logging on web too
    if (__DEV__) {
      try {
        const { enableIndexedDbPersistence, enableMultiTabIndexedDbPersistence } = require('firebase/firestore');
        console.log('🔍 Firestore logging enabled for web (check browser console)');
      } catch (error) {
        console.log('⚠️ Firestore logging setup skipped');
      }
    }
  } else {
    // Import React Native Firebase modules dynamically for native platforms (modular API)
    try {
      const rnFirebase = require('@react-native-firebase/app').default;
      const { getFirestore } = require('@react-native-firebase/firestore');
      const { getAuth } = require('@react-native-firebase/auth');
      const { getFunctions } = require('@react-native-firebase/functions');
      const {
        getAnalytics: getNativeAnalytics,
        logEvent: nativeLogEvent,
        setAnalyticsCollectionEnabled: setNativeAnalyticsCollectionEnabled,
      } = require('@react-native-firebase/analytics');
      
      // Initialize Firebase for native if not already initialized
      if (rnFirebase.apps.length === 0) {
        rnFirebase.initializeApp(firebaseConfig);
      }
      
      firebaseApp = rnFirebase;
      firestore = getFirestore(); // Modular API
      auth = getAuth(); // Modular API
      functions = getFunctions(); // Modular API
      analytics = getNativeAnalytics();
      getAnalyticsInstance = () => analytics || getNativeAnalytics();
      logAnalyticsEvent = (eventName: string, params?: Record<string, any>) =>
        nativeLogEvent(getAnalyticsInstance(), eventName, params);
      setAnalyticsCollectionEnabledFn = (enabled: boolean) =>
        setNativeAnalyticsCollectionEnabled(getAnalyticsInstance(), enabled);
      
      // Enable Firestore debug logging to see actual SDK operations
      if (__DEV__) {
        try {
          const { setLogLevel } = require('@react-native-firebase/firestore');
          setLogLevel('debug');
          console.log('🔍 Firestore debug logging ENABLED');
        } catch (error) {
          console.log('⚠️ Could not enable Firestore logging:', error);
        }
      }
      
      // Connect to Functions emulator in development for native platforms
      // const useLocalFunctions = process.env.EXPO_PUBLIC_USE_LOCAL_FUNCTIONS === 'true';
      
      // if (useLocalFunctions) {
      //   functions.useFunctionsEmulator('http://localhost:5001');
      //   console.log('✅ Connected native to Functions emulator at localhost:5001');
      // }
      
      console.log('Firebase initialized successfully for native');
    } catch (error) {
      console.error("Failed to initialize native Firebase:", error);
      console.log('Falling back to web Firebase initialization...');
      initWebFirebase();
    }
  }
} catch (error) {
  console.error("Error initializing Firebase:", error);
  // Provide fallback empty objects to prevent undefined errors
  auth = null;
  firestore = null;
  database = null;
  functions = null;
  analytics = null;
  getAnalyticsInstance = () => null;
  logAnalyticsEvent = () => {};
  setAnalyticsCollectionEnabledFn = () => {};
}

export {
  firebaseApp,
  firestore,
  auth,
  functions,
  analytics,
  getAnalyticsInstance,
  logAnalyticsEvent,
  setAnalyticsCollectionEnabledFn as setAnalyticsCollectionEnabled,
};

// Add db alias for compatibility
export const db = firestore;

// Export modular Firestore functions (unified for web and native)
let collection: any, doc: any, getDoc: any, getDocs: any, setDoc: any, updateDoc: any, deleteDoc: any, addDoc: any;
let query: any, where: any, orderBy: any, limit: any, onSnapshot: any, Timestamp: any;
let signInAnonymously: any, signOut: any, onAuthStateChanged: any, updateProfile: any, getIdToken: any;
let signInWithEmailAndPassword: any, createUserWithEmailAndPassword: any, signInWithCustomToken: any;
let sendPasswordResetEmail: any;
let httpsCallable: any;
let signInWithCredential: any, signInWithPopup: any, signInWithRedirect: any, getRedirectResult: any, OAuthProvider: any, AppleAuthProvider: any, GoogleAuthProvider: any;

if (Platform.OS === 'web') {
  // Web Firebase SDK (already modular)
  collection = webCollection;
  doc = webDoc;
  getDoc = webGetDoc;
  getDocs = webGetDocs;
  setDoc = webSetDoc;
  updateDoc = webUpdateDoc;
  deleteDoc = webDeleteDoc;
  addDoc = webAddDoc;
  query = webQuery;
  where = webWhere;
  orderBy = webOrderBy;
  limit = webLimit;
  onSnapshot = webOnSnapshot;
  Timestamp = webTimestamp;
  
  const authModule = require('firebase/auth');
  signInAnonymously = authModule.signInAnonymously;
  signOut = authModule.signOut;
  onAuthStateChanged = authModule.onAuthStateChanged;
  updateProfile = authModule.updateProfile;
  getIdToken = authModule.getIdToken;
  signInWithEmailAndPassword = authModule.signInWithEmailAndPassword;
  createUserWithEmailAndPassword = authModule.createUserWithEmailAndPassword;
  signInWithCustomToken = authModule.signInWithCustomToken;
  sendPasswordResetEmail = authModule.sendPasswordResetEmail;
  signInWithCredential = authModule.signInWithCredential;
  signInWithPopup = authModule.signInWithPopup;
  signInWithRedirect = authModule.signInWithRedirect;
  getRedirectResult = authModule.getRedirectResult;
  OAuthProvider = authModule.OAuthProvider;
  AppleAuthProvider = authModule.OAuthProvider; // Apple uses generic OAuth provider on web
  GoogleAuthProvider = authModule.GoogleAuthProvider;
  
  const functionsModule = require('firebase/functions');
  httpsCallable = functionsModule.httpsCallable;
} else {
  // React Native Firebase (modular API)
  const firestoreModule = require('@react-native-firebase/firestore');
  collection = firestoreModule.collection;
  doc = firestoreModule.doc;
  getDoc = firestoreModule.getDoc;
  getDocs = firestoreModule.getDocs;
  setDoc = firestoreModule.setDoc;
  updateDoc = firestoreModule.updateDoc;
  deleteDoc = firestoreModule.deleteDoc;
  addDoc = firestoreModule.addDoc;
  query = firestoreModule.query;
  where = firestoreModule.where;
  orderBy = firestoreModule.orderBy;
  limit = firestoreModule.limit;
  onSnapshot = firestoreModule.onSnapshot;
  Timestamp = firestoreModule.Timestamp;
  
  // For React Native Firebase v23+, create wrapper functions to match web Firebase API signature
  // Web signature: signInAnonymously(auth) -> Native: auth().signInAnonymously()
  signInAnonymously = (authInstance: any) => authInstance.signInAnonymously();
  signOut = (authInstance: any) => authInstance.signOut();
  onAuthStateChanged = (authInstance: any, callback: any) => authInstance.onAuthStateChanged(callback);
  updateProfile = (user: any, profile: any) => user.updateProfile(profile);
  getIdToken = (user: any) => user.getIdToken();
  signInWithEmailAndPassword = (authInstance: any, email: string, password: string) => 
    authInstance.signInWithEmailAndPassword(email, password);
  createUserWithEmailAndPassword = (authInstance: any, email: string, password: string) => 
    authInstance.createUserWithEmailAndPassword(email, password);
  signInWithCustomToken = (authInstance: any, token: string) => 
    authInstance.signInWithCustomToken(token);
  sendPasswordResetEmail = (authInstance: any, email: string) => 
    authInstance.sendPasswordResetEmail(email);
  signInWithCredential = (authInstance: any, credential: any) => 
    authInstance.signInWithCredential(credential);
  
  // For Apple/Google providers, import the classes
  const authModule = require('@react-native-firebase/auth');
  AppleAuthProvider = authModule.AppleAuthProvider;
  GoogleAuthProvider = authModule.GoogleAuthProvider;
  
  const functionsModule = require('@react-native-firebase/functions');
  httpsCallable = functionsModule.httpsCallable;
}

// Export all modular functions
export {
  // Firestore functions
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  // Auth functions
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updateProfile,
  getIdToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  OAuthProvider,
  AppleAuthProvider,
  GoogleAuthProvider,
  // Functions
  httpsCallable,
}; 